import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { loadStripe } from "@stripe/stripe-js";
import { getProductImage } from "../utils/imageHelper";
import { createCheckoutSession, getCountries, calculateShipping } from "../services/api";
import "./Checkout.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    address: "",
    city: "",
    postalCode: "",
    country: "FR",
    phone: "",
    referralSource: "",
  });

  const [errors, setErrors] = useState({});

  const [deliveryMethod, setDeliveryMethod] = useState("home"); // "home" ou "pickup"
  const [pickupLocation, setPickupLocation] = useState("");
  // Coût de la livraison à domicile. `null` = pas encore calculé (évite d'afficher "Gratuit" par erreur).
  const [homeShippingCost, setHomeShippingCost] = useState(null);
  const [isShippingLoading, setIsShippingLoading] = useState(false);

  // Charger la liste des pays
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.data.countries || []);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error("Erreur chargement pays:", error);
        setCountries([]);
      }
    };
    loadCountries();
  }, []);

  // Calculer les frais de port de la livraison à domicile quand le pays ou le panier changent.
  // Volontairement indépendant de `deliveryMethod` : basculer entre "domicile" et "retrait"
  // ne doit jamais déclencher un nouvel appel réseau, seul un changement réel du panier ou
  // du pays justifie un recalcul.
  useEffect(() => {
    if (cart.length === 0) {
      setHomeShippingCost(0);
      setIsShippingLoading(false);
      return;
    }

    let isCancelled = false;
    setIsShippingLoading(true);

    const fetchShippingCost = async () => {
      const items = cart.map((item) => ({
        product: item.product._id,
        size: item.size,
        quantity: item.quantity,
      }));

      try {
        const response = await calculateShipping({
          countryCode: formData.country,
          items,
        });
        if (isCancelled) return;
        setHomeShippingCost(response.data.shippingCost);
      } catch (error) {
        if (isCancelled) return;
        if (process.env.NODE_ENV === 'development') console.error("Erreur calcul frais de port:", error);
        setHomeShippingCost(0);
      } finally {
        if (!isCancelled) setIsShippingLoading(false);
      }
    };

    fetchShippingCost();

    // Ignore une réponse qui arriverait après qu'un nouveau calcul ait été lancé
    // (évite d'écraser le prix avec une réponse obsolète).
    return () => {
      isCancelled = true;
    };
  }, [formData.country, cart]);

  // Coût réellement appliqué selon le mode de livraison choisi
  const shippingCost = deliveryMethod === "pickup" ? 0 : homeShippingCost ?? 0;
  const isHomeShippingUnknown = deliveryMethod === "home" && homeShippingCost === null;

  // Obtenir le texte descriptif des frais de port
  const getShippingDescription = () => {
    if (deliveryMethod === "pickup") {
      return "Gratuit";
    }

    if (cart.length === 0) return "Calculé selon vos articles";

    const itemsCount = cart.reduce((count, item) => count + item.quantity, 0);
    return `${itemsCount} article${itemsCount > 1 ? "s" : ""}`;
  };

  const totalWithShipping = getCartTotal() + shippingCost;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Gestionnaire spécifique pour les champs numériques
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    // Ne garder que les chiffres, espaces, +, -, (, )
    const sanitizedValue = value.replace(/[^\d\s+\-()]/g, "");
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    });
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Gestionnaire pour le code postal (uniquement chiffres)
  const handlePostalCodeChange = (e) => {
    const { name, value } = e.target;
    // Ne garder que les chiffres
    const sanitizedValue = value.replace(/\D/g, "");
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    });
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    // Validation du nom complet
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Le nom complet est requis";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Le nom doit contenir au moins 3 caractères";
    }

    // Validations spécifiques à la livraison à domicile
    if (deliveryMethod === "home") {
      if (!formData.address.trim()) {
        newErrors.address = "L'adresse est requise";
      } else if (formData.address.trim().length < 5) {
        newErrors.address = "L'adresse doit contenir au moins 5 caractères";
      }

      if (!formData.city.trim()) {
        newErrors.city = "La ville est requise";
      } else if (formData.city.trim().length < 2) {
        newErrors.city = "Le nom de la ville est trop court";
      }

      if (!formData.postalCode.trim()) {
        newErrors.postalCode = "Le code postal est requis";
      } else {
        // Validation basique du code postal (France: 5 chiffres)
        const postalRegex = /^[0-9]{4,10}$/;
        if (!postalRegex.test(formData.postalCode.trim())) {
          newErrors.postalCode = "Code postal invalide";
        }
      }

      if (!formData.country) {
        newErrors.country = "Le pays est requis";
      }
    }

    // Validation du téléphone
    if (!formData.phone.trim()) {
      newErrors.phone = "Le téléphone est requis";
    } else {
      // Validation basique du numéro de téléphone (au moins 10 chiffres)
      const phoneRegex = /^[\d\s+\-()]{10,}$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = "Numéro de téléphone invalide (minimum 10 chiffres)";
      }
    }

    // Validation pour le retrait au Crossfit
    if (deliveryMethod === "pickup" && !pickupLocation) {
      alert("Veuillez sélectionner un point de retrait");
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      alert("Votre panier est vide");
      return;
    }

    if (isHomeShippingUnknown) {
      alert("Calcul des frais de port en cours, veuillez patienter quelques instants.");
      return;
    }

    // Valider le formulaire avant de continuer
    if (!validateForm()) {
      // Scroller jusqu'à la première erreur
      const firstErrorField = document.querySelector(".form-group.error");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setLoading(true);

    try {
      const items = cart.map((item) => ({
        product: item.product._id,
        name: item.product.name,
        size: item.size,
        color: item.color,
        quantity: item.quantity,
        price: item.product.price,
      }));


      // Pour les retraits en magasin, envoyer une adresse factice
      const shippingData = deliveryMethod === "pickup" 
        ? {
            fullName: formData.fullName,
            address: "Retrait en boutique",
            city: pickupLocation,
            postalCode: "77410",
            country: "FR",
            phone: formData.phone
          }
        : formData;
      
      const response = await createCheckoutSession({
        items,
        shippingAddress: shippingData,
        email: formData.email,
        deliveryMethod,
        pickupLocation: deliveryMethod === "pickup" ? pickupLocation : null,
        shippingCost,
        referralSource: formData.referralSource,
      });

      // Utiliser l'URL directe au lieu de redirectToCheckout (méthode recommandée)
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.sessionId) {
        // Fallback avec redirectToCheckout si l'URL n'est pas fournie
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: response.data.sessionId,
        });

        if (error) {
          if (process.env.NODE_ENV === 'development') console.error("❌ Erreur Stripe:", error);
          alert("Erreur lors de la redirection vers le paiement");
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("❌ Erreur:", error);
      const errorMessage = error.response?.data?.message || "Erreur lors de la création de la session de paiement";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate("/cart");
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="page-title">Finaliser la commande</h1>

        <div className="checkout-layout">
          <form className="checkout-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Informations de contact</h2>
              <div className={`form-group ${errors.email ? "error" : ""}`}>
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Ex: votre.email@example.com"
                  required
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label>Comment avez-vous entendu parler de nous ?</label>
                <select
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Sélectionner votre réponse</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Salle de sport">Salle de sport</option>
                  <option value="Connaissances">Connaissances</option>
                  <option value="Compétitions">Compétitions</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h2>Mode de livraison</h2>
              <div className="delivery-options">
                <label
                  className={`delivery-option ${
                    deliveryMethod === "home" ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="home"
                    checked={deliveryMethod === "home"}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                  />
                  <div className="option-content">
                    <div className="option-left">
                      <span className="option-title">
                        📦 Livraison à domicile
                      </span>
                      <span className="option-subtitle">
                        {getShippingDescription()}
                      </span>
                    </div>
                    <span className="option-price">
                      {homeShippingCost === null
                        ? isShippingLoading
                          ? "Calcul..."
                          : "Calculé"
                        : homeShippingCost > 0
                        ? `${homeShippingCost.toFixed(2)}€`
                        : "Gratuit"}
                    </span>
                  </div>
                </label>

                <label
                  className={`delivery-option ${
                    deliveryMethod === "pickup" ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="pickup"
                    checked={deliveryMethod === "pickup"}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                  />
                  <div className="option-content">
                    <div className="option-left">
                      <span className="option-title">
                        🏪 Retrait au Crossfit
                      </span>
                      <span className="option-subtitle">
                        CrossFit Claye ou CrossFit Diversity
                      </span>
                    </div>
                    <span className="option-price option-price-free">
                      Gratuit
                    </span>
                  </div>
                </label>
              </div>

              {deliveryMethod === "pickup" && (
                <div className="form-group" style={{ marginTop: "16px" }}>
                  <label>Choisissez votre Crossfit *</label>
                  <select
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required
                  >
                    <option value="">Choisissez un point de retrait</option>
                    <option value="CrossFit Claye">CrossFit Claye</option>
                    <option value="CrossFit Diversity">
                      CrossFit Diversity
                    </option>
                  </select>
                  {pickupLocation && (
                    <div className="pickup-info-message">
                      ℹ️ Seulement pour les adhérents du club {pickupLocation}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-section">
              <h2>
                {deliveryMethod === "pickup"
                  ? "Coordonnées"
                  : "Adresse de livraison"}
              </h2>
              <div className={`form-group ${errors.fullName ? "error" : ""}`}>
                <label>Nom complet *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
                {errors.fullName && (
                  <span className="error-message">{errors.fullName}</span>
                )}
              </div>
              {deliveryMethod === "home" && (
                <>
                  <div
                    className={`form-group ${errors.address ? "error" : ""}`}
                  >
                    <label>Adresse *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                    {errors.address && (
                      <span className="error-message">{errors.address}</span>
                    )}
                  </div>
                  <div className="form-row">
                    <div className={`form-group ${errors.city ? "error" : ""}`}>
                      <label>Ville *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                      {errors.city && (
                        <span className="error-message">{errors.city}</span>
                      )}
                    </div>
                    <div
                      className={`form-group ${
                        errors.postalCode ? "error" : ""
                      }`}
                    >
                      <label>Code postal *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handlePostalCodeChange}
                        placeholder="Ex: 75001"
                        required
                      />
                      {errors.postalCode && (
                        <span className="error-message">
                          {errors.postalCode}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`form-group ${errors.country ? "error" : ""}`}
                  >
                    <label>Pays *</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                    >
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.europeanUnion ? "🇪🇺 " : ""}
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <span className="error-message">{errors.country}</span>
                    )}
                  </div>
                </>
              )}
              <div className={`form-group ${errors.phone ? "error" : ""}`}>
                <label>Téléphone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleNumericChange}
                  placeholder="Ex: +33 6 12 34 56 78"
                  required
                />
                {errors.phone && (
                  <span className="error-message">{errors.phone}</span>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary submit-btn"
              disabled={loading || isHomeShippingUnknown}
            >
              {loading
                ? "Chargement..."
                : isHomeShippingUnknown
                ? "Calcul des frais de port..."
                : "Procéder au paiement"}
            </button>
          </form>

          <div className="order-summary">
            <h2>Récapitulatif</h2>
            <div className="summary-items">
              {cart.map((item, index) => (
                <div
                  key={`${item.product._id}-${index}`}
                  className="summary-item"
                >
                  <img
                    src={
                      getProductImage(item.product.images[0]) ||
                      "https://via.placeholder.com/60x75/07004e/5efafa?text=PR"
                    }
                    alt={item.product.name}
                  />
                  <div className="summary-item-details">
                    <p className="item-name">{item.product.name}</p>
                    <p className="item-variant">
                      {item.size} | {item.color} | x{item.quantity}
                    </p>
                  </div>
                  <p className="item-total">
                    {(item.product.price * item.quantity).toFixed(2)}€
                  </p>
                </div>
              ))}
            </div>
            <div className="summary-totals">
              <div className="summary-line">
                <span>Sous-total</span>
                <span>{getCartTotal().toFixed(2)}€</span>
              </div>
              <div
                className={`summary-line ${
                  deliveryMethod === "pickup" ? "summary-line-free" : ""
                }`}
              >
                <span>
                  {deliveryMethod === "pickup"
                    ? "🏪 Retrait en boutique"
                    : "📦 Livraison"}
                  {deliveryMethod === "home" && shippingCost > 0 && (
                    <span className="shipping-detail">
                      {" "}
                      ({getShippingDescription()})
                    </span>
                  )}
                </span>
                <span className={!isHomeShippingUnknown && shippingCost === 0 ? "free-shipping" : ""}>
                  {isHomeShippingUnknown
                    ? "Calcul..."
                    : shippingCost > 0
                    ? `${shippingCost.toFixed(2)}€`
                    : "Gratuit"}
                </span>
              </div>
              <div className="summary-total">
                <span>Total à payer</span>
                <span>{totalWithShipping.toFixed(2)}€</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
